import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, TextField, Button, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, Typography, InputAdornment,
  Skeleton, Alert, IconButton, useMediaQuery, Snackbar, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions, Tooltip,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Search, Forest, ChevronRight, Add, Edit, Delete, SearchOff,
  ViewList, ViewModule, Star, Park,
} from '@mui/icons-material';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import EmptyState from '../components/EmptyState';
import FlaecheFormDialog, {
  FlaecheForm, emptyFlaecheForm, flaecheToForm, formToPayload,
} from '../components/FlaecheFormDialog';

// Zustand-Farbe für Flächen (gut / mittel / schlecht)
function getFlaecheZustandColor(z: string | null): string {
  switch ((z || '').toLowerCase()) {
    case 'gut': return '#2e7d32';
    case 'mittel': return '#f9a825';
    case 'schlecht': return '#c62828';
    default: return '#78909c';
  }
}

// m² → lesbare Flächenangabe (ab 1 ha in Hektar)
function formatFlaeche(qm: number | null): string {
  if (qm == null) return '–';
  const n = Number(qm);
  if (n >= 10000) return `${(n / 10000).toLocaleString('de-DE', { maximumFractionDigits: 2 })} ha`;
  return `${n.toLocaleString('de-DE', { maximumFractionDigits: 0 })} m²`;
}

// Karte für Mobile
function FlaecheCard({ f, onClick }: { f: any; onClick: () => void }) {
  const erfasst = Number(f.anzahl_baeume_erfasst || 0);
  const geschaetzt = Number(f.geschaetzte_anzahl || 0);
  const gesamt = erfasst + geschaetzt;
  return (
    <Box onClick={onClick} sx={{
      p: 1.5, borderRadius: 2, bgcolor: 'white', cursor: 'pointer',
      border: '1px solid', borderColor: '#e8e8e8',
      borderLeft: '4px solid', borderLeftColor: getFlaecheZustandColor(f.zustand),
      display: 'flex', alignItems: 'center', gap: 1.5,
      '&:active': { bgcolor: '#f5f5f5' },
    }}>
      <Box sx={{
        width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
        bgcolor: getFlaecheZustandColor(f.zustand) + '18',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Forest sx={{ color: getFlaecheZustandColor(f.zustand), fontSize: 22 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ fontSize: '0.88rem' }} noWrap>
            {f.name}
          </Typography>
          {f.typ && <Chip label={f.typ} size="small" color="primary" sx={{ height: 18, fontSize: '0.6rem', fontWeight: 700 }} />}
          {Number(f.offene_massnahmen) > 0 && (
            <Chip icon={<Star sx={{ fontSize: '12px !important' }} />} label={f.offene_massnahmen} size="small"
              sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 700, '& .MuiChip-icon': { color: '#1565c0' } }} />
          )}
          {Number(f.offene_baum_massnahmen) > 0 && (
            <Chip icon={<Park sx={{ fontSize: '12px !important' }} />} label={f.offene_baum_massnahmen} size="small"
              sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, '& .MuiChip-icon': { color: '#2e7d32' } }} />
          )}
        </Box>
        <Typography variant="body2" color="text.secondary" noWrap sx={{ fontSize: '0.78rem' }}>
          {gesamt > 0 ? `ca. ${gesamt} Bäume` : 'keine Angabe'}
          {(f.arten && f.arten.length > 0) ? ` · ${f.arten.slice(0, 3).join(', ')}` : ''}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontSize: '0.72rem' }}>
          {[f.strasse, f.ort].filter(Boolean).join(', ') || '–'} · {formatFlaeche(f.flaeche_qm)}
        </Typography>
      </Box>
      <ChevronRight sx={{ color: '#ccc', fontSize: 20, flexShrink: 0 }} />
    </Box>
  );
}

export default function Flaechen() {
  const navigate = useNavigate();
  const { canEdit, isAdmin } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [flaechen, setFlaechen] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<'table' | 'cards'>(isMobile ? 'cards' : 'table');
  const [snackbar, setSnackbar] = useState('');

  // Anlegen / Bearbeiten
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FlaecheForm>({ ...emptyFlaecheForm });
  const [saving, setSaving] = useState(false);

  // Löschen
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { setViewMode(isMobile ? 'cards' : 'table'); }, [isMobile]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/flaechen');
      setFlaechen(res.data);
      setError('');
    } catch {
      setError('Flächen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = flaechen.filter(f => {
    if (!search) return true;
    const s = search.toLowerCase();
    return [f.name, f.typ, f.ort, f.strasse, ...(f.arten || [])]
      .filter(Boolean)
      .some((v: string) => String(v).toLowerCase().includes(s));
  });

  const openCreate = () => { setEditId(null); setForm({ ...emptyFlaecheForm }); setDialogOpen(true); };
  const openEdit = (f: any) => { setEditId(f.id); setForm(flaecheToForm(f)); setDialogOpen(true); };

  const handleSave = async () => {
    if (!form.name.trim()) { setSnackbar('Name erforderlich'); return; }
    setSaving(true);
    try {
      const payload = formToPayload(form);
      if (editId) {
        await api.put(`/flaechen/${editId}`, payload);
        setSnackbar('Fläche aktualisiert');
      } else {
        await api.post('/flaechen', payload);
        setSnackbar('Fläche angelegt');
      }
      setDialogOpen(false);
      load();
    } catch {
      setSnackbar('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await api.delete(`/flaechen/${deleteId}`);
      setSnackbar('Fläche gelöscht');
      setDeleteId(null);
      load();
    } catch {
      setSnackbar('Fehler beim Löschen');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, flex: 1 }}>Waldflächen</Typography>
        {!isMobile && (
          <IconButton size="small" onClick={() => setViewMode(viewMode === 'table' ? 'cards' : 'table')}
            sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
            title={viewMode === 'table' ? 'Kartenansicht' : 'Tabellenansicht'}>
            {viewMode === 'table' ? <ViewModule /> : <ViewList />}
          </IconButton>
        )}
        {canEdit && (
          <Button variant="contained" startIcon={<Add />} onClick={openCreate} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Neue Fläche
          </Button>
        )}
      </Box>

      {/* Suche */}
      <TextField
        fullWidth size="small" placeholder="Suche nach Name, Typ, Ort, Art…"
        value={search} onChange={e => setSearch(e.target.value)}
        sx={{ mb: 1.5 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><Search sx={{ color: 'text.disabled' }} /></InputAdornment> }}
      />

      {/* Ergebnis-Zähler */}
      <Box sx={{ bgcolor: '#e8f5e9', borderRadius: 1.5, px: 2, py: 0.8, mb: 1.5 }}>
        <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500, fontSize: '0.82rem' }}>
          {loading ? 'Lade…' : `${filtered.length.toLocaleString('de-DE')} ${filtered.length === 1 ? 'Fläche' : 'Flächen'}`}
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Leerzustand */}
      {!loading && filtered.length === 0 && (
        search ? (
          <EmptyState icon={SearchOff} title="Keine Fläche gefunden"
            description="Mit dieser Suche wurde keine passende Fläche gefunden."
            accentColor="info" />
        ) : (
          <EmptyState icon={Forest} title="Noch keine Waldflächen erfasst"
            description="Lege deine erste Fläche an. Das Polygon zeichnest du danach auf der Karte ein."
            action={canEdit ? { label: 'Neue Fläche anlegen', icon: Add, onClick: openCreate } : undefined}
            accentColor="primary" />
        )
      )}

      {/* Tabellenansicht */}
      {viewMode === 'table' && (loading || filtered.length > 0) && (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Typ</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Ort</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', textAlign: 'right' }}>Größe</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', textAlign: 'right' }}>Bäume (ca.)</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem' }}>Zustand</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', textAlign: 'right', width: 90 }}>Aktion</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>{Array.from({ length: 7 }).map((_, j) => <TableCell key={j}><Skeleton /></TableCell>)}</TableRow>
                ))
              ) : (
                filtered.map(f => {
                  const erfasst = Number(f.anzahl_baeume_erfasst || 0);
                  const gesamt = erfasst + Number(f.geschaetzte_anzahl || 0);
                  return (
                    <TableRow key={f.id} hover sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/flaechen/${f.id}`)}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.7 }}>
                          <Forest sx={{ fontSize: 18, color: getFlaecheZustandColor(f.zustand) }} />
                          <Typography fontWeight={700} sx={{ fontSize: '0.85rem' }}>{f.name}</Typography>
                          {Number(f.offene_massnahmen) > 0 && (
                            <Tooltip title="Offene Maßnahmen an der Fläche">
                              <Chip icon={<Star sx={{ fontSize: '13px !important' }} />} label={f.offene_massnahmen} size="small"
                                sx={{ height: 20, fontSize: '0.66rem', bgcolor: '#e3f2fd', color: '#1565c0', fontWeight: 700, '& .MuiChip-icon': { color: '#1565c0' } }} />
                            </Tooltip>
                          )}
                          {Number(f.offene_baum_massnahmen) > 0 && (
                            <Tooltip title="Offene Maßnahmen an Bäumen in dieser Fläche">
                              <Chip icon={<Park sx={{ fontSize: '13px !important' }} />} label={f.offene_baum_massnahmen} size="small"
                                sx={{ height: 20, fontSize: '0.66rem', bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 700, '& .MuiChip-icon': { color: '#2e7d32' } }} />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontSize: '0.82rem' }}>{f.typ || '–'}</TableCell>
                      <TableCell sx={{ fontSize: '0.82rem' }}>{[f.strasse, f.ort].filter(Boolean).join(', ') || '–'}</TableCell>
                      <TableCell sx={{ fontSize: '0.82rem', textAlign: 'right' }}>{formatFlaeche(f.flaeche_qm)}</TableCell>
                      <TableCell sx={{ fontSize: '0.82rem', textAlign: 'right' }}>
                        {gesamt > 0 ? `ca. ${gesamt}` : '–'}
                        {erfasst > 0 && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            ({erfasst} erf.)
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: getFlaecheZustandColor(f.zustand), flexShrink: 0 }} />
                          <Typography sx={{ fontSize: '0.82rem' }}>{f.zustand || '–'}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ textAlign: 'right' }} onClick={e => e.stopPropagation()}>
                        {canEdit && (
                          <Tooltip title="Bearbeiten">
                            <IconButton size="small" onClick={() => openEdit(f)}><Edit fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                        {isAdmin && (
                          <Tooltip title="Löschen">
                            <IconButton size="small" onClick={() => setDeleteId(f.id)} sx={{ color: '#c62828' }}><Delete fontSize="small" /></IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Kartenansicht */}
      {viewMode === 'cards' && !loading && filtered.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {filtered.map(f => <FlaecheCard key={f.id} f={f} onClick={() => navigate(`/flaechen/${f.id}`)} />)}
        </Box>
      )}

      {/* Anlegen / Bearbeiten */}
      <FlaecheFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave}
        form={form}
        setForm={setForm}
        saving={saving}
        isEdit={!!editId}
      />

      {/* Löschen bestätigen */}
      <Dialog open={!!deleteId} onClose={() => !deleting && setDeleteId(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Fläche löschen</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Die Fläche wird mit allen Kontrollen und Fotos gelöscht. Erfasste Einzelbäume bleiben erhalten – sie sind nur räumlich verknüpft, nicht Teil der Fläche.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteId(null)} disabled={deleting}>Abbrechen</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting}>
            {deleting ? <CircularProgress size={20} /> : 'Löschen'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snackbar} autoHideDuration={4000} onClose={() => setSnackbar('')}
        message={snackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} />
    </Box>
  );
}
